/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useParkingLotData.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for loading Parking Lot data.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchParkingLotById } from '../api/parkingLotsApi';
import { createDefaultFormData } from '../utils/formDefaults';
import { generateContextFromName } from '../utils/contextHelpers';
import type { ParkingLotFormState } from '../types';

interface UseParkingLotDataOptions {
  id?: string;
  duplicateId?: string | null;
  isEditMode: boolean;
}

export function useParkingLotData({
  id,
  duplicateId,
  isEditMode
}: UseParkingLotDataOptions) {
  const location = useLocation();
  const [formData, setFormData] = useState<ParkingLotFormState>(createDefaultFormData);
  const [isLoading, setIsLoading] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');

  // Load parking lot data if editing or duplicating
  const loadParkingLot = useCallback(async (lotId: string, duplicate: boolean = false) => {
    setIsLoading(true);
    try {
      const lot = await fetchParkingLotById(lotId);

      const settings = (lot.settings as Record<string, any>) || {};

      const loadedData: ParkingLotFormState = {
        name: lot.name || '',
        extension: lot.extension || '',
        totalPositions: lot.totalPositions ?? 10,
        parkingTime: lot.parkingTime ?? 45,
        comebackTime: lot.comebackTime ?? 30,
        findSlot: lot.findSlot || 'next',
        courtesyTone: lot.courtesyTone || 'caller',
        mohClass: lot.mohClass || 'default',
        returnToOriginator: lot.returnToOriginator ?? true,
        announcePosition: lot.announcePosition ?? true,
        callTransfer: lot.callTransfer ?? false,
        callReparking: lot.callReparking ?? false,
        callHangup: lot.callHangup ?? false,
        record: lot.record ?? false,
        announcementPath: lot.announcementPath || '',
        timeoutModule: lot.timeoutModule || '',
        timeoutDestination: lot.timeoutDestination || '',
        enabled: lot.enabled ?? true,
        settings
      };

      // If duplicating, clear extension and modify name
      if (duplicate) {
        loadedData.extension = '';
        if (loadedData.name) {
          loadedData.name = `${loadedData.name} Copy`.trim();
        }
      }

      setFormData(loadedData);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);



  // Load parking lot when id or duplicateId changes
  useEffect(() => {
    const currentId = id || 'new';
    const savedDataKey = 'parkingLots:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (isEditMode && id && id !== 'new') {
      if (lastLoadedIdRef.current !== id) {
        loadParkingLot(id);
        lastLoadedIdRef.current = id;
        lastLocationKeyRef.current = locationKey;
      }
    } else if (!isEditMode && duplicateId) {
      if (lastLoadedIdRef.current !== duplicateId) {
        loadParkingLot(duplicateId, true);
        lastLoadedIdRef.current = duplicateId;
        lastLocationKeyRef.current = locationKey;
      }
    } else if (!isEditMode && !duplicateId) {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ?
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;

      const shouldReset = lastLoadedIdRef.current !== 'new' ||
        lastLocationKeyRef.current !== locationKey ||
        (lastLoadedIdRef.current === 'new' && currentId === 'new' &&
          resetTimestamp && resetTimestamp !== lastResetTimestamp);

      if (shouldReset) {
        // Clear any saved data to ensure fresh form
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }

        // Reset form to defaults
        setFormData(createDefaultFormData());
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;
      }
    }
  }, [id, isEditMode, duplicateId, loadParkingLot, location.key, location.pathname, location.search]);

  return {
    formData,
    setFormData,
    isLoading,
    loadParkingLot
  };
}

